import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import '../styles/Portfolio.css';

const Portfolio = ({ translations, language }) => {
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [selectedCategory, setSelectedCategory] = useState('all');

  const portfolioData = {
    en: {
      title: 'Portfolio',
      subtitle: 'Success Stories & Case Studies',
      categories: ['All', 'E-commerce', 'SaaS', 'Healthcare', 'Finance'],
      projects: [
        {
          id: 1,
          title: 'E-commerce Growth Campaign',
          category: 'E-commerce',
          description: 'Increased online sales by 400% through strategic SEO and paid advertising.',
          metrics: { traffic: '+350%', conversions: '+400%', roi: '500%' },
          image: 'ecommerce',
          tags: ['SEO', 'Google Ads', 'Social Media']
        },
        {
          id: 2,
          title: 'SaaS Lead Generation',
          category: 'SaaS',
          description: 'Generated 1000+ qualified leads monthly for B2B SaaS platform.',
          metrics: { leads: '+1000', cost: '-60%', quality: '+85%' },
          image: 'Dev',
          tags: ['LinkedIn Ads', 'Content Marketing', 'Email Automation']
        },
        {
          id: 3,
          title: 'Healthcare Digital Transformation',
          category: 'Healthcare',
          description: 'Modernized healthcare provider\'s digital presence and patient acquisition.',
          metrics: { patients: '+250%', bookings: '+180%', satisfaction: '95%' },
          image: 'health',
          tags: ['Local SEO', 'Reputation Management', 'PPC']
        },
        {
          id: 4,
          title: 'Fintech App Launch',
          category: 'Finance',
          description: 'Successfully launched fintech app with 50K+ downloads in first month.',
          metrics: { downloads: '50K+', retention: '78%', rating: '4.8★' },
          image: 'Fintech',
          tags: ['App Store Optimization', 'Influencer Marketing', 'PR']
        }
      ]
    },
    ar: {
      title: 'الأعمال',
      subtitle: 'قصص النجاح ودراسات الحالة',
      categories: ['الكل', 'التجارة الإلكترونية', 'البرمجيات', 'الرعاية الصحية', 'المالية'],
      projects: [
        {
          id: 1,
          title: 'حملة نمو التجارة الإلكترونية',
          category: 'E-commerce',
          description: 'زيادة المبيعات عبر الإنترنت بنسبة 400% من خلال تحسين محركات البحث والإعلانات المدفوعة الاستراتيجية.',
          metrics: { 'حركة المرور': '+350%', 'التحويلات': '+400%', 'العائد': '500%' },
          image: 'ecommerce',
          tags: ['تحسين محركات البحث', 'إعلانات جوجل', 'وسائل التواصل الاجتماعي']
        },
        {
          id: 2,
          title: 'توليد العملاء المحتملين للبرمجيات',
          category: 'SaaS',
          description: 'توليد أكثر من 1000 عميل محتمل مؤهل شهرياً لمنصة البرمجيات B2B.',
          metrics: { 'العملاء المحتملين': '+1000', 'التكلفة': '-60%', 'الجودة': '+85%' },
          image: 'Dev',
          tags: ['إعلانات لينكد إن', 'تسويق المحتوى', 'أتمتة البريد الإلكتروني']
        },
        {
          id: 3,
          title: 'التحول الرقمي للرعاية الصحية',
          category: 'Healthcare',
          description: 'تحديث الحضور الرقمي لمقدم الرعاية الصحية واكتساب المرضى.',
          metrics: { 'المرضى': '+250%', 'الحجوزات': '+180%', 'الرضا': '95%' },
          image: 'health',
          tags: ['تحسين محركات البحث المحلية', 'إدارة السمعة', 'الدفع لكل نقرة']
        },
        {
          id: 4,
          title: 'إطلاق تطبيق التكنولوجيا المالية',
          category: 'Finance',
          description: 'إطلاق ناجح لتطبيق التكنولوجيا المالية مع أكثر من 50 ألف تحميل في الشهر الأول.',
          metrics: { 'التحميلات': '50K+', 'الاحتفاظ': '78%', 'التقييم': '4.8★' },
          image: 'Fintech',
          tags: ['تحسين متجر التطبيقات', 'تسويق المؤثرين', 'العلاقات العامة']
        }
      ]
    }
  };

  const data = portfolioData[language];

  const filteredProjects = selectedCategory === 'all' 
    ? data.projects 
    : data.projects.filter(project => project.category.toLowerCase() === selectedCategory.toLowerCase());

  return (
    <section id="portfolio" className="portfolio-section section" ref={ref}>
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="section-title gradient-text">{data.title}</h2>
          <p className="section-subtitle">{data.subtitle}</p>
        </motion.div>

        <motion.div
          className="portfolio-filters"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {data.categories.map((category, index) => (
            <motion.button
              key={index}
              className={`filter-btn ${selectedCategory === (index === 0 ? 'all' : category.toLowerCase()) ? 'active' : ''}`}
              onClick={() => setSelectedCategory(index === 0 ? 'all' : category.toLowerCase())}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {category}
            </motion.button>
          ))}
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={selectedCategory}
            className="portfolio-grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                className="portfolio-card glass-morphism"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                whileHover={{ y: -10 }}
              >
                <div className="portfolio-image">
                  <img 
                    src={project.image === 'Fintech' ? require(`../imges/Fintech .webp`) : require(`../imges/${project.image}.jpg`)} 
                    alt={project.title}
                    className="project-image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className={`project-placeholder ${project.image}`} style={{display: 'none'}}>
                    <i className="fas fa-chart-line"></i>
                  </div>
                  <div className="portfolio-overlay">
                    <motion.button
                      className="view-btn"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <i className="fas fa-eye"></i>
                    </motion.button>
                  </div>
                </div>

                <div className="portfolio-content">
                  <h3 className="portfolio-title">{project.title}</h3>
                  <p className="portfolio-description">{project.description}</p>

                  <div className="portfolio-metrics">
                    {Object.entries(project.metrics).map(([key, value]) => (
                      <div key={key} className="metric-item">
                        <span className="metric-value">{value}</span>
                        <span className="metric-label">{key}</span>
                      </div>
                    ))}
                  </div>

                  <div className="portfolio-tags">
                    {project.tags.map((tag, tagIndex) => (
                      <span key={tagIndex} className="tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="portfolio-glow"></div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        <motion.div
          className="portfolio-cta"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <motion.button
            className="btn-futuristic primary"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            {language === 'en' ? 'View All Case Studies' : 'عرض جميع دراسات الحالة'}
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Portfolio;